/**
 * Parsing for `From`/`To`-style RFC 5322 address headers, split out of
 * gmail.ts so that file stays focused on the API calls themselves.
 */

/** One address parsed out of a `From`/`To` header. */
export interface GmailRecipient {
  name: string;
  email: string;
}

export function parseFromHeader(from: string): GmailRecipient {
  const match = from.match(/^(.*?)\s*<([^>]+)>/);
  if (match) {
    return { name: match[1].trim().replace(/^"|"$/g, ""), email: match[2] };
  }
  return { name: from, email: from };
}

/**
 * Split a `To`/`Cc`-style header into its individual addresses. A plain
 * `String.split(",")` breaks on display names that quote a comma (e.g.
 * `"Doe, John" <john@example.com>`), so commas inside quotes or inside
 * the `<...>` address itself don't count as separators.
 */
function splitAddressList(header: string): string[] {
  const parts: string[] = [];
  let current = "";
  let inQuotes = false;
  let inAngleBrackets = false;
  for (const char of header) {
    if (char === '"') inQuotes = !inQuotes;
    if (char === "<") inAngleBrackets = true;
    if (char === ">") inAngleBrackets = false;
    if (char === "," && !inQuotes && !inAngleBrackets) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current);
  return parts;
}

export function parseRecipients(header: string): GmailRecipient[] {
  if (!header.trim()) return [];
  return splitAddressList(header)
    .map((part) => part.trim())
    .filter(Boolean)
    .map(parseFromHeader);
}
